import React, { useState, useContext, ChangeEvent, useEffect } from 'react';
import { FormGroup, Collapse, Box } from '@material-ui/core';
import { PasslySelect, PasslyCheckbox } from '@app/components/Forms';
import { SIGNIN_ALGOS, PROTOCOL_VERSIONS, TOKEN_LIFETIME, ISigninAlgoValue, ISigninAlgo } from '@app/pages/ssoManager/addEditApplication.enums';
import { AddAudienceUriComponent } from '../components/addAudienceUri.component';
import { isSpInit, isIdPInit, isWSFed, isCustomApp, tokenLifetimeToInt, additionalAudiencesPresent } from '@app/helpers/addEditApplication.helpers';
import AppsContext from '../appContext';
import { isValidUrl } from '@app/helpers';
import { ExpandLess, ExpandMore } from '@material-ui/icons';
import { useTypedTranslation } from '@app/hooks';
import { PasslyTextField } from '@app/components';
import { InputNumber } from '@itglue/kaseya-ui';

interface IComponentLocalState {
    includeExtraAudiences: boolean;
    tokenLifetime: number;
    allowAditionalAudiences: boolean;
    protocolVersion: string;
    signMessage: boolean;
    signAssertion: boolean;
    signingAlgo: ISigninAlgoValue | '';
    fixedRelayState: string;
    fixedWsFedContext: string;
}

interface IProps {
    setProtocolConfigError: Function;
}

export const ProtocolConfigurationForm: React.SFC<IProps> = ({ setProtocolConfigError }: IProps) => {
    const { translation } = useTypedTranslation();

    const app = useContext(AppsContext);
    const { ReplyToUrl, Protocol, AudienceUris, ServiceIssuerUri, IdentityIssuerUri, ServiceConfiguration, AccessTokenLifetime, updateState } = app;

    const [
        {
            includeExtraAudiences,
            allowAditionalAudiences,
            tokenLifetime,
            protocolVersion,
            signMessage,
            signAssertion,
            signingAlgo,
            fixedRelayState,
            fixedWsFedContext
        },
        setState
    ] = useState<IComponentLocalState>({
        includeExtraAudiences: false,
        tokenLifetime: tokenLifetimeToInt(AccessTokenLifetime) ?? TOKEN_LIFETIME,
        allowAditionalAudiences: additionalAudiencesPresent(AudienceUris as string[], ReplyToUrl) ?? false,
        protocolVersion: '',
        signMessage: true,
        signAssertion: true,
        signingAlgo: '',
        fixedRelayState: '',
        fixedWsFedContext: ''
    });
    const [openAdvancedSettings, setOpenAdvancedSettings] = useState<boolean>(false);

    const [errors, setErrors] = useState<{ tokenLifetime: boolean; replyToUrl: boolean }>({ tokenLifetime: false, replyToUrl: false });

    useEffect(() => {
        if (app.Id && ServiceConfiguration) {
            let config = JSON.parse(ServiceConfiguration);

            if (typeof config === 'string') {
                config = {};
            }

            if ('Saml.SignMessage' in config) {
                setState((prevState) => ({ ...prevState, signMessage: config['Saml.SignMessage'] }));
            }

            if ('Saml.SignAssertion' in config) {
                setState((prevState) => ({ ...prevState, signAssertion: config['Saml.SignAssertion'] }));
            }

            if ('Saml.RelayState' in config) {
                setState((prevState) => ({ ...prevState, fixedRelayState: config['Saml.RelayState'] }));
            }

            if ('WsFed.Context' in config) {
                setState((prevState) => ({ ...prevState, fixedWsFedContext: config['WsFed.Context'] }));
            }

            if ('Token.IncludeAudiences' in config) {
                setState((prevState) => ({ ...prevState, includeExtraAudiences: config['Token.IncludeAudiences'] }));
            }

            if ('Token.SignatureAlgorithm' in config) {
                const algo = config['Token.SignatureAlgorithm'];

                SIGNIN_ALGOS.forEach(function (a: ISigninAlgo) {
                    if (algo === a.value.signature) {
                        setState((prevState) => ({ ...prevState, signingAlgo: a.value }));
                    }
                });
            }

            if ('WS.ProtocolVersion' in config) {
                const version = config['WS.ProtocolVersion'];

                PROTOCOL_VERSIONS.forEach(function (v) {
                    if (version === v.value) {
                        setState((prevState) => ({ ...prevState, protocolVersion: v.value }));
                    }
                });
            }
        }
    }, []);

    useEffect(() => {
        const lifetimeString = `PT${tokenLifetime}M`;
        updateState('AccessTokenLifetime', lifetimeString);
        updateState('RefreshTokenLifetime', lifetimeString);
    }, [tokenLifetime]);

    useEffect(() => {
        setProtocolConfigError(errors.tokenLifetime || errors.replyToUrl);
    }, [errors.tokenLifetime, errors.replyToUrl]);

    useEffect(() => {
        let config: any = JSON.parse(ServiceConfiguration);
        if (isSpInit(Protocol) || isIdPInit(Protocol)) {
            if (isSpInit(Protocol)) {
                config['Saml.RelayState'] = fixedRelayState;
                config['Token.IncludeAudiences'] = includeExtraAudiences;
            }
            if (signingAlgo !== '') {
                config['Token.SignatureAlgorithm'] = signingAlgo.signature;
                config['Token.DigestAlgorithm'] = signingAlgo.digest;
            }

            config['Saml.SignMessage'] = signMessage;
            config['Saml.SignAssertion'] = signAssertion;
        }

        if (isWSFed(Protocol)) {
            if (protocolVersion !== '') {
                config['WS.ProtocolVersion'] = protocolVersion;
            }

            config['WsFed.Context'] = fixedWsFedContext;
        }

        let configToSave = JSON.stringify(config);
        updateState('ServiceConfiguration', configToSave);
    }, [Protocol, protocolVersion, signingAlgo, includeExtraAudiences, fixedWsFedContext, fixedRelayState, signAssertion, signMessage]);

    // FUNCTIONS
    const handleInclueExtraAudiencesCheckbox = (checked: boolean) => {
        setState((prevState) => ({ ...prevState, includeExtraAudiences: checked }));
    };

    const handleMultipleAudiencesCheckbox = () => {
        setState((prevState) => ({ ...prevState, allowAditionalAudiences: !prevState.allowAditionalAudiences }));
    };

    const onTokenChange = (e: any) => {
        const token = +e.target.value;

        setErrors((prevState) => ({ ...prevState, [e.target.name]: token > TOKEN_LIFETIME }));
        setState((prevState) => ({ ...prevState, [e.target.name]: token }));
    };

    const onProtocolVersionChange = ({ target: { value } }: React.ChangeEvent<{ name?: string | undefined; value: unknown }>) => {
        if (value) setState((prevState) => ({ ...prevState, protocolVersion: value as string }));
    };

    const handleSignMessageCheckbox = (checked: boolean) => {
        setState((prevState) => ({ ...prevState, signMessage: checked }));
    };

    const handleSignAssertionCheckbox = (checked: boolean) => {
        setState((prevState) => ({ ...prevState, signAssertion: checked }));
    };

    const onSigningAlgoChange = ({ target: { value } }: ChangeEvent<{ name?: string | undefined; value: unknown }>) => {
        setState((prevState) => ({ ...prevState, signingAlgo: value as ISigninAlgoValue }));
    };

    const onFixedRelayChange = ({ target: { value } }: ChangeEvent<HTMLInputElement>) => {
        setState((prevState) => ({ ...prevState, fixedRelayState: value }));
    };

    const onFixedWsFedContextChange = ({ target: { value } }: ChangeEvent<HTMLInputElement>) => {
        setState((prevState) => ({ ...prevState, fixedWsFedContext: value }));
    };

    const replyToChanged = (keyName: string, value: string) => {
        if (AudienceUris && AudienceUris.length === 1) {
            updateState('AudienceUris', [ReplyToUrl]);
        } else if (AudienceUris && AudienceUris.length > 1) {
            setState((prevState) => ({ ...prevState, allowAditionalAudiences: true }));
        }

        if (isValidUrl(value)) {
            setErrors((prevState) => ({ ...prevState, replyToUrl: false }));
        } else {
            setErrors((prevState) => ({ ...prevState, replyToUrl: true }));
        }
        updateState(keyName, value);
    };

    return (
        <>
            {isSpInit(Protocol) ? (
                <div className='form__row'>
                    {/* ASSERTION_CONSUMER_SERVICE */}
                    <PasslyTextField
                        name='ReplyToUrl'
                        label={'APP_ACS'}
                        value={ReplyToUrl}
                        onChange={({ target: { name, value } }) => replyToChanged(name, value)}
                        id='assertionConsumerUri-input'
                        error={errors.replyToUrl}
                        helperText={errors.replyToUrl && translation('INVALID_URL')}
                        fullWidth
                    />
                </div>
            ) : (
                <div className='form__row'>
                    {/* REPLY_TO_URI */}
                    <PasslyTextField
                        name='ReplyToUrl'
                        label={'APP_REPLY_TO_URL'}
                        value={ReplyToUrl}
                        onChange={({ target: { name, value } }) => updateState(name, value)}
                        id='replyToUri-input'
                        error={errors.replyToUrl}
                        helperText={errors.replyToUrl && translation('INVALID_URL')}
                        fullWidth
                    />
                </div>
            )}
            {/* ALLOW_MULTIPLE_AUDIENCES */}
            {isSpInit(Protocol) && (
                <PasslyCheckbox
                    name='multipleAudiences'
                    checked={allowAditionalAudiences}
                    onChange={handleMultipleAudiencesCheckbox}
                    label={translation('ALLOW_ADDITIONAL_AUDIENCES')}
                    disabled={additionalAudiencesPresent(AudienceUris as string[], ReplyToUrl)}
                />
            )}
            {/* ADD_ADDITIONAL_AUDIENCES */}
            {/* ADD_AUDIENCE_URI */}
            {(allowAditionalAudiences || isIdPInit(Protocol) || isWSFed(Protocol)) && (
                <FormGroup className='protocol-configuration__form--extended'>
                    <AddAudienceUriComponent audienceUriList={AudienceUris as string[]} onAdd={(name, value) => updateState(name, value)} />
                </FormGroup>
            )}
            {/* SERVICE_URI */}
            {isSpInit(Protocol) && (
                <div className='form__row'>
                    <PasslyTextField
                        name='ServiceIssuerUri'
                        label={'APP_SERVICE_ISSUER'}
                        value={ServiceIssuerUri ?? ''}
                        onChange={({ target: { name, value } }) => updateState(name, value)}
                        id='serviceIssuerUri-input'
                        fullWidth
                    />
                </div>
            )}

            {/* IDENTITY_ISSUER */}
            {(isIdPInit(Protocol) || isSpInit(Protocol)) && !isCustomApp(app) && (
                <div className='form__row'>
                    <PasslyTextField
                        name='IdentityIssuerUri'
                        label={'APP_IDENTITY_ISSUER'}
                        value={IdentityIssuerUri}
                        onChange={({ target: { name, value } }) => updateState(name, value)}
                        id='identityIssuerUri-input'
                        fullWidth
                    />
                </div>
            )}

            {/* TOKEN */}
            <div className='form__row'>
                <InputNumber
                    id='tokenLifetime-input'
                    name='tokenLifetime'
                    label={translation('APP_TOKEN_LIFETIME')}
                    value={tokenLifetime}
                    onValueChange={onTokenChange}
                    showButtons
                    idecrementButtonClassName={''}
                    errorMessage={errors.tokenLifetime ? translation('TOKEN_LIFETIME_MAX_ERROR') : undefined}
                />
            </div>
            <Box display='flex' flexDirection='row' mt={2} onClick={() => setOpenAdvancedSettings(!openAdvancedSettings)} style={{ cursor: 'pointer' }}>
                {openAdvancedSettings ? <ExpandLess /> : <ExpandMore />}
                <Box>
                    <h3>{translation('ADVANCED_SETTINGS')}</h3>
                    {!openAdvancedSettings && <p>{translation('PROTOCOL_ADVANCED_SETTINGS')}</p>}
                </Box>
            </Box>
            <Collapse in={openAdvancedSettings} timeout='auto'>
                <FormGroup>
                    {/* INLCUDE EXTRA AUDIENCES */}
                    {
                        <>
                            {isSpInit(Protocol) && (
                                <PasslyCheckbox
                                    name='includeExtraAudiences'
                                    checked={includeExtraAudiences}
                                    onChange={handleInclueExtraAudiencesCheckbox}
                                    label={translation('INCLUDE_EXTRA_AUDIENCES')}
                                />
                            )}
                            {/* SIGN MESSAGE */}
                            <PasslyCheckbox
                                name='signMessage'
                                checked={signMessage}
                                onChange={handleSignMessageCheckbox}
                                label={translation('SIGN_MESSAGE')}
                            />
                            {/* SIGN ASSERTION */}
                            <PasslyCheckbox
                                name='signAssertion'
                                checked={signAssertion}
                                onChange={handleSignAssertionCheckbox}
                                label={translation('SIGN_ASSERTION')}
                            />
                            {/* SIGN ALGOS */}
                            <div className='form__row'>
                                <PasslySelect
                                    disabled={SIGNIN_ALGOS?.length === 0}
                                    name='signAlgo'
                                    value={signingAlgo}
                                    id='signAlgo-input'
                                    onChange={onSigningAlgoChange}
                                    label='APP_SIGNING_ALGO'
                                    options={SIGNIN_ALGOS}
                                />
                            </div>
                            {/* FIXED RELAY STATE */}
                            {isSpInit(Protocol) && (
                                <div className='form__row'>
                                    <PasslyTextField
                                        name='fixedRelayState'
                                        label={'FIXED_RELAY_STATE'}
                                        value={fixedRelayState}
                                        id='fixedRelayState-input'
                                        onChange={onFixedRelayChange}
                                        fullWidth
                                    />
                                </div>
                            )}
                        </>
                    }
                    {isWSFed(Protocol) && (
                        <>
                            {/* PROTOCOL_VERSION */}
                            <div className='form__row'>
                                <PasslySelect
                                    disabled={PROTOCOL_VERSIONS?.length === 0}
                                    name='protocolVersion'
                                    value={protocolVersion}
                                    id='protocolVersion-input'
                                    onChange={onProtocolVersionChange}
                                    label='APP_WS_PROTOCOL_VERSION'
                                    options={PROTOCOL_VERSIONS}
                                />
                            </div>
                            {/* FIXED_CONTENT */}
                            <div className='form__row'>
                                <PasslyTextField
                                    name='fixedWsFedContext'
                                    label={'FIXED_WSFED_CONTEXT'}
                                    value={fixedWsFedContext}
                                    id='fixedWsFedContext-input'
                                    onChange={onFixedWsFedContextChange}
                                    fullWidth
                                />
                            </div>
                        </>
                    )}
                </FormGroup>
            </Collapse>
        </>
    );
};
